import { useEffect, useState, useRef } from 'react';
import * as ivyChatApi from '@/services/ivyChatApi';

interface WorkspaceIvySession {
  chatId: string | null;
  isLoading: boolean;
  error: string | null;
}

// Module-level map to prevent concurrent resolves for the same conversationId
const inflightResolves = new Map<string, Promise<string>>();

export function useWorkspaceIvySession(
  conversationId: string | undefined,
  conversationTitle: string | undefined,
  authReady: boolean,
  qualityTier?: string,
): WorkspaceIvySession {
  const [chatId, setChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolvedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!conversationId || !authReady) return;
    if (resolvedRef.current === conversationId) return;

    let cancelled = false;

    async function resolve() {
      setIsLoading(true);
      setError(null);

      try {
        // Deduplicate concurrent resolves for the same conversationId
        let resolvePromise = inflightResolves.get(conversationId!);
        if (!resolvePromise) {
          resolvePromise = resolveSession(conversationId!, conversationTitle, qualityTier);
          inflightResolves.set(conversationId!, resolvePromise);
          resolvePromise.finally(() => inflightResolves.delete(conversationId!));
        }

        const resolvedChatId = await resolvePromise;
        if (cancelled) return;
        setChatId(resolvedChatId);
        resolvedRef.current = conversationId!;
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [conversationId, conversationTitle, authReady, qualityTier]);

  return { chatId, isLoading, error };
}

async function resolveSession(conversationId: string, title?: string, qualityTier?: string): Promise<string> {
  const existing = await ivyChatApi.getSessionByConversation(conversationId);
  if (existing) return existing.chatId;

  const session = await ivyChatApi.createSession(title || 'Video Project', conversationId, qualityTier);
  return session.chatId;
}
